import { createContext, useCallback, useContext, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import { Button } from '../ds'

interface ConfirmOptions {
  title: string
  message?: ReactNode
  /** Confirm button label. @default "Удалить" */
  confirmLabel?: string
  /** Red confirm button. @default true */
  danger?: boolean
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn>(async () => false)

export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext)
}

// Императивное подтверждение на Radix AlertDialog: `await confirm({...})` → boolean.
// Заменяет нативный window.confirm() стилизованной модалкой (та же визуальная база, что и Modal).
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>(
    (next) =>
      new Promise<boolean>((resolve) => {
        resolver.current = resolve
        setOpts(next)
      }),
    []
  )

  const settle = (result: boolean) => {
    resolver.current?.(result)
    resolver.current = null
    setOpts(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog.Root open={opts !== null} onOpenChange={(open) => !open && settle(false)}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="modal-backdrop confirm-backdrop" />
          <AlertDialog.Content className="modal confirm-modal">
            <div className="modal-head">
              <AlertDialog.Title>{opts?.title}</AlertDialog.Title>
            </div>
            <div className="modal-body">
              {opts?.message ? (
                <AlertDialog.Description className="confirm-text">{opts.message}</AlertDialog.Description>
              ) : null}
              <div className="confirm-actions">
                <AlertDialog.Cancel asChild>
                  <Button variant="secondary" onClick={() => settle(false)}>
                    Отмена
                  </Button>
                </AlertDialog.Cancel>
                <AlertDialog.Action asChild>
                  <Button variant={opts?.danger === false ? 'primary' : 'danger'} onClick={() => settle(true)}>
                    {opts?.confirmLabel ?? 'Удалить'}
                  </Button>
                </AlertDialog.Action>
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </ConfirmContext.Provider>
  )
}
